import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth, getReturnTo, RETURN_TO_KEY } from "@/context/AuthContext";
import { BookingProvider } from "@/context/BookingContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { LocaleProvider } from "@/context/LocaleContext";
import AppLayout from "@/components/AppLayout";
import CookieConsent from "@/components/CookieConsent";
import Analytics from "@/components/Analytics";
import Clarity from "@/components/Clarity";
import SiteChatWidget from "@/components/SiteChatWidget";
import { Loader2 } from "lucide-react";

import Landing from "@/pages/Landing";
import About from "@/pages/About";
import Solution from "@/pages/Solution";
import AicomAIPage from "@/pages/AicomAIPage";
import AiAvatarPage from "@/pages/AiAvatarPage";
import PaidSocialPage from "@/pages/PaidSocialPage";
import SeoPage from "@/pages/SeoPage";
import ThoughtLeadershipPage from "@/pages/ThoughtLeadershipPage";
import StrategyPage from "@/pages/StrategyPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import SecurityPage from "@/pages/SecurityPage";
import DPAPage from "@/pages/DPAPage";
import Inspiration from "@/pages/Inspiration";
import GuidePage from "@/pages/GuidePage";
import GuideThankYou from "@/pages/GuideThankYou";
import BudgetCalculator from "@/pages/BudgetCalculator";
import ArticlePage from "@/pages/ArticlePage";
import Auth from "@/pages/Auth";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Weekly from "@/pages/Weekly";
import Ideas from "@/pages/Ideas";
import Brand from "@/pages/Brand";
import LinkedInPage from "@/pages/LinkedIn";
import FacebookPage from "@/pages/Facebook";
import DebatePage from "@/pages/Debate";
import Newsletter from "@/pages/Newsletter";
import Press from "@/pages/Press";
import Campaigns from "@/pages/Campaigns";
import Lead from "@/pages/Lead";
import Projects from "@/pages/Projects";
import Knowledge from "@/pages/Knowledge";
import Settings from "@/pages/Settings";
import Pricing from "@/pages/Pricing";
import AllPlans from "@/pages/AllPlans";
import OnboardingWizard from "@/pages/OnboardingWizard";
import PaymentSuccess from "@/pages/PaymentSuccess";
import AdminLayout from "@/components/AdminLayout";
import AdminOverview from "@/pages/admin/Overview";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminLeads from "@/pages/admin/AdminLeads";
import AdminTracking from "@/pages/admin/AdminTracking";
import AdminIdeaMail from "@/pages/admin/AdminIdeaMail";

function FullLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  );
}

function RequireAuth() {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullLoader />;
  if (!user) {
    if (location.search) sessionStorage.setItem(RETURN_TO_KEY, location.pathname + location.search);
    return <Navigate to="/login" replace />;
  }
  sessionStorage.removeItem(RETURN_TO_KEY);
  return <Outlet />;
}

function RequireOnboarded() {
  const { user } = useAuth();
  if (user && user.role !== "admin" && !user.onboarded) return <Navigate to="/kom-i-gang" replace />;
  return <Outlet />;
}

function RequireAdmin() {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return <Outlet />;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (user) {
    const dest = user.role === "admin" ? "/admin" : (user.onboarded ? (getReturnTo() || "/dashboard") : "/kom-i-gang");
    return <Navigate to={dest} replace />;
  }
  return children;
}

function App() {
  return (
    <div className="App">
      <Toaster theme="dark" position="top-right" richColors />
      <AuthProvider>
        <ThemeProvider>
        <LocaleProvider>
        <BrowserRouter>
          <BookingProvider>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/om" element={<About />} />
            <Route path="/privatlivspolitik" element={<PrivacyPage />} />
            <Route path="/handelsbetingelser" element={<TermsPage />} />
            <Route path="/sikkerhed-og-databehandling" element={<SecurityPage />} />
            <Route path="/databehandleraftale" element={<DPAPage />} />
            <Route path="/losninger/aicom-ai" element={<AicomAIPage />} />
            <Route path="/planer" element={<AllPlans />} />
            <Route path="/kom-i-gang" element={<OnboardingWizard />} />
            <Route path="/losninger/ai-avatar" element={<AiAvatarPage />} />
            <Route path="/losninger/paid-social" element={<PaidSocialPage />} />
            <Route path="/losninger/seo" element={<SeoPage />} />
            <Route path="/losninger/thought-leadership" element={<ThoughtLeadershipPage />} />
            <Route path="/losninger/strategi" element={<StrategyPage />} />
            <Route path="/losninger/:slug" element={<Solution />} />
            <Route path="/inspiration" element={<Inspiration />} />
            <Route path="/inspiration/:slug" element={<GuidePage />} />
            <Route path="/guide/tak" element={<GuideThankYou />} />
            <Route path="/beregner" element={<BudgetCalculator />} />
            <Route path="/artikler/:slug" element={<ArticlePage />} />
            <Route path="/login" element={<PublicOnly><Auth mode="login" /></PublicOnly>} />
            <Route path="/register" element={<Navigate to="/kom-i-gang" replace />} />
            <Route path="/glemt-adgangskode" element={<ForgotPassword />} />
            <Route path="/nulstil-adgangskode" element={<ResetPassword />} />

            <Route element={<RequireAuth />}>
              <Route path="/betaling/success" element={<PaymentSuccess />} />
              <Route element={<RequireOnboarded />}>
                <Route element={<AppLayout />}>
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/ugens-forslag" element={<Weekly />} />
                  <Route path="/idebank" element={<Ideas />} />
                  <Route path="/brandfortaelling" element={<Brand />} />
                  <Route path="/linkedin" element={<LinkedInPage />} />
                  <Route path="/facebook" element={<FacebookPage />} />
                  <Route path="/debatindlaeg" element={<DebatePage />} />
                  <Route path="/nyhedsbrev" element={<Newsletter />} />
                  <Route path="/presse" element={<Press />} />
                  <Route path="/kampagner" element={<Campaigns />} />
                  <Route path="/leadgenerator" element={<Lead />} />
                  <Route path="/projekter" element={<Projects />} />
                  <Route path="/vidensbank" element={<Knowledge />} />
                  <Route path="/indstillinger" element={<Settings />} />
                  <Route path="/priser" element={<Pricing />} />
                </Route>
              </Route>
            </Route>

            <Route element={<RequireAdmin />}>
              <Route element={<AdminLayout />}>
                <Route path="/admin" element={<AdminOverview />} />
                <Route path="/admin/brugere" element={<AdminUsers />} />
                <Route path="/admin/henvendelser" element={<AdminLeads />} />
                <Route path="/admin/tracking" element={<AdminTracking />} />
                <Route path="/admin/mandagsmail" element={<AdminIdeaMail />} />
              </Route>
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <CookieConsent />
          <Analytics />
          <Clarity />
          <SiteChatWidget />
          </BookingProvider>
        </BrowserRouter>
        </LocaleProvider>
        </ThemeProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
